import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { setCredentials } from './authSlice'
import { useGoogleLoginMutation } from './authApiSlice'
import { GOOGLE_CLIENT_ID } from '../../config/google'

const GSI_SRC = 'https://accounts.google.com/gsi/client'

// Loads Google Identity Services once per page
let gsiPromise
const loadGsi = () => {
    if (window.google?.accounts?.id) return Promise.resolve()
    if (!gsiPromise) {
        gsiPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = GSI_SRC
            script.async = true
            script.onload = resolve
            script.onerror = () => {
                gsiPromise = undefined
                reject(new Error('Could not load Google sign-in'))
            }
            document.head.appendChild(script)
        })
    }
    return gsiPromise
}

/**
 * "Continue with Google" button. Signs in, or creates the account on first
 * use; the backend links it to an existing account with the same email.
 * Renders nothing when no client id is configured.
 */
const GoogleSignIn = ({ onError }) => {
    const { t, i18n } = useTranslation()
    const buttonRef = useRef(null)
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [googleLogin] = useGoogleLoginMutation()
    const [failed, setFailed] = useState(false)

    // The GSI callback is registered once; keep it pointing at fresh props
    const handleCredential = useRef()
    handleCredential.current = async ({ credential }) => {
        try {
            const { accessToken } = await googleLogin({ credential }).unwrap()
            // Choosing Google is choosing to stay signed in on this device
            localStorage.setItem('persist', 'true')
            dispatch(setCredentials({ accessToken }))
            navigate('/dash')
        } catch (err) {
            onError?.(err.status ? t('auth.errors.googleFailed') : t('auth.errors.noServerResponse'))
        }
    }

    useEffect(() => {
        if (!GOOGLE_CLIENT_ID) return undefined
        let cancelled = false

        loadGsi()
            .then(() => {
                if (cancelled || !buttonRef.current) return
                window.google.accounts.id.initialize({
                    client_id: GOOGLE_CLIENT_ID,
                    callback: (response) => handleCredential.current(response),
                    ux_mode: 'popup'
                })
                window.google.accounts.id.renderButton(buttonRef.current, {
                    type: 'standard',
                    theme: 'filled_black',
                    size: 'large',
                    shape: 'pill',
                    text: 'continue_with',
                    locale: i18n.language,
                    width: Math.min(buttonRef.current.offsetWidth || 320, 400)
                })
            })
            .catch(() => !cancelled && setFailed(true))

        return () => { cancelled = true }
    }, [i18n.language])

    if (!GOOGLE_CLIENT_ID || failed) return null

    return (
        <div className="auth__google">
            <div className="auth__divider" role="separator"><span>{t('auth.or')}</span></div>
            <div ref={buttonRef} className="auth__google-button" />
        </div>
    )
}

export default GoogleSignIn
